import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  Article as ArticleIcon,
  HelpOutline as HelpIcon,
  AccountBalance as AccountBalanceIcon,
  School as SchoolIcon,
  Inventory2 as AssetIcon,
  Style as FlashcardIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  TrackChanges as AssessmentIcon,
  Quiz as QuizIcon,
  Code as CodeIcon,
  AccountTree as RepoIcon,
  Mic as MicIcon,
  People as PeopleIcon,
  HourglassEmpty as PendingIcon,
  FiberManualRecord as OnlineIcon,
  BarChart as AnalyticsIcon,
  EmojiEvents as TrophyIcon,
  Support as SupportIcon,
  Flag as FlagIcon,
  Feedback as FeedbackIcon,
  ContactMail as EnquiryIcon,
  Business as OfficeIcon,
  AttachMoney as MoneyIcon,
  Alarm as AlarmIcon,
  Home as HomeIcon,
  FitnessCenter as PracticeIcon,
  MenuBook as LearnIcon,
  Build as ToolsIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Lightbulb as LightbulbIcon,
  Logout as LogoutIcon,
  Assignment as AssignmentIcon,
  Class as ClassIcon,
} from '@mui/icons-material';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isStudyMode, setIsStudyMode] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check for study mode from localStorage
  useEffect(() => {
    const checkStudyMode = () => {
      const isCourseRoute = location.pathname.startsWith('/course/');
      const studyModeFromStorage = localStorage.getItem('courseStudyMode') === 'true';
      const darkModeFromStorage = localStorage.getItem('studyModeDarkMode') === 'true';
      setIsStudyMode(isCourseRoute && studyModeFromStorage);
      setIsDarkMode(isCourseRoute && studyModeFromStorage && darkModeFromStorage);
    };
    
    checkStudyMode();
    
    // Listen for storage changes
    window.addEventListener('storage', checkStudyMode);
    
    // Poll for changes (for same-tab updates)
    const interval = setInterval(checkStudyMode, 100);
    
    return () => {
      window.removeEventListener('storage', checkStudyMode);
      clearInterval(interval);
    };
  }, [location.pathname]);

  if (!user) return null;
  if (isStudyMode) return null;

  const DropdownMenu = ({ name, icon, label, children }) => (
    <div className={`nav-dropdown ${openDropdown === name ? 'active' : ''}`}>
      <button 
        className="nav-dropdown-trigger"
        onClick={() => toggleDropdown(name)}
        aria-expanded={openDropdown === name}
      >
        <span className="dropdown-icon">{icon}</span>
        {label}
        <svg 
          className="dropdown-arrow" 
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className="nav-dropdown-menu">
        {children}
      </div>
    </div>
  );

  return (
    <>
      <nav 
        className="navbar" 
        ref={dropdownRef}
        style={{
          background: isStudyMode && isDarkMode ? '#1a202c' : '',
          borderBottom: isStudyMode && isDarkMode ? '1px solid #2d3748' : ''
        }}
      >
        <div className="navbar-content">
          <h1 style={{ color: isStudyMode && isDarkMode ? '#e2e8f0' : '' }}>DCT App</h1>
          
          {!isStudyMode && (
            <>
              {/* Hamburger Icon */}
              <button 
                className="hamburger"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                <span className={isMenuOpen ? 'active' : ''}></span>
                <span className={isMenuOpen ? 'active' : ''}></span>
                <span className={isMenuOpen ? 'active' : ''}></span>
              </button>

              <div className={`navbar-actions ${isMenuOpen ? 'active' : ''}`}>
            {user.role === 'admin' ? (
              <>
                <Link to="/admin" className="nav-link" onClick={closeMenu}>
                  <span className="nav-icon"><DashboardIcon /></span>
                  Dashboard
                </Link>
                
                <DropdownMenu name="content" icon={<ArticleIcon />} label="Content">
                  <Link to="/questions" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><HelpIcon /></span>
                    Questions
                  </Link>
                  <Link to="/question-bank" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><AccountBalanceIcon /></span>
                    Question Bank
                  </Link>
                  <Link to="/courses" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><SchoolIcon /></span>
                    Courses
                  </Link>
                  <Link to="/asset-library" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><AssetIcon /></span>
                    Asset Library
                  </Link>
                  <Link to="/admin/flashcards" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><FlashcardIcon /></span>
                    Flashcards
                  </Link>
                  <Link to="/code-snippets" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><CodeIcon /></span>
                    Code Snippets
                  </Link>
                  <Link to="/live-code-editor" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><CodeIcon /></span>
                    Live Code Editor
                  </Link>
                  <Link to="/admin/ai-instruction-templates" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><DescriptionIcon /></span>
                    AI Instruction Templates
                  </Link>
                  <Link to="/drive" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><FolderIcon /></span>
                    Drive
                  </Link>
                </DropdownMenu>

                <DropdownMenu name="assessments" icon={<AssessmentIcon />} label="Assessments">
                  <Link to="/admin/quizzes" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><QuizIcon /></span>
                    Quizzes
                  </Link>
                  <Link to="/admin/coding-assignments" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><CodeIcon /></span>
                    Coding Assignments
                  </Link>
                  <Link to="/admin/code-repos" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><RepoIcon /></span>
                    Code Repos
                  </Link>
                  <Link to="/interviews" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><MicIcon /></span>
                    Interviews
                  </Link>
                </DropdownMenu>

                <DropdownMenu name="users" icon={<PeopleIcon />} label="Users">
                  <Link to="/students" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><SchoolIcon /></span>
                    Students
                  </Link>
                  <Link to="/groups" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><PeopleIcon /></span>
                    Groups
                  </Link>
                  <Link to="/pending-users" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><PendingIcon /></span>
                    Pending Approvals
                  </Link>
                  <Link to="/online-users" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><OnlineIcon /></span>
                    Online Users
                  </Link>
                </DropdownMenu>

                <DropdownMenu name="analytics" icon={<AnalyticsIcon />} label="Analytics">
                  <Link to="/leaderboard" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><TrophyIcon /></span>
                    Leaderboard
                  </Link>
                </DropdownMenu>

                <DropdownMenu name="support" icon={<SupportIcon />} label="Support">
                  <Link to="/admin/issues" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><FlagIcon /></span>
                    Question Issues
                  </Link>
                  <Link to="/admin/feedback" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><FeedbackIcon /></span>
                    User Feedback
                  </Link>
                </DropdownMenu>

                <DropdownMenu name="office" icon={<OfficeIcon />} label="Office">
                  <Link to="/admin/enquiries" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><EnquiryIcon /></span>
                    Student Enquiries
                  </Link>
                  <Link to="/admin/fee-management" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><MoneyIcon /></span>
                    Fee Management
                  </Link>
                  <Link to="/admin/fee-dashboard" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><DashboardIcon /></span>
                    Fee Dashboard
                  </Link>
                  <Link to="/admin/pending-fees" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><AlarmIcon /></span>
                    Fee Reminders
                  </Link>
                  <Link to="/admin/bank-accounts" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><AccountBalanceIcon /></span>
                    Bank Accounts
                  </Link>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link 
                  to="/user" 
                  className="nav-link"
                  onClick={closeMenu}
                >
                  <span className="nav-icon"><HomeIcon /></span>
                  Dashboard
                </Link>
                
                <DropdownMenu name="practice" icon={<PracticeIcon />} label="Practice">
                  <Link to="/my-quizzes" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><QuizIcon /></span>
                    My Quizzes
                  </Link>
                  <Link to="/my-coding-assignments" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><CodeIcon /></span>
                    Coding Assignments
                  </Link>
                  <Link to="/my-code-repos" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><RepoIcon /></span>
                    My Code Repos
                  </Link>
                  <Link to="/my-interviews" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><MicIcon /></span>
                    My Interviews
                  </Link>
                </DropdownMenu>
                
                <DropdownMenu name="learn" icon={<LearnIcon />} label="Learn">
                  <Link to="/my-courses" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><ClassIcon /></span>
                    My Courses
                  </Link>
                  <Link to="/flashcards" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><FlashcardIcon /></span>
                    Flashcards
                  </Link>
                  <Link to="/question-bank" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><TrophyIcon /></span>
                    Question Bank
                  </Link>
                </DropdownMenu>
                
                <DropdownMenu name="tools" icon={<ToolsIcon />} label="Tools">
                  <Link to="/resumes" className="dropdown-link" onClick={closeMenu}>
                    <span className="dropdown-link-icon"><DescriptionIcon /></span>
                    Resume Builder
                  </Link>
                </DropdownMenu>
              </>
            )}
            
            <DropdownMenu name="user" icon={<PersonIcon />} label={user.name}>
              <Link to="/profile" className="dropdown-link" onClick={closeMenu}>
                <span className="dropdown-link-icon"><SettingsIcon /></span>
                Profile
              </Link>
              <Link to="/my-feedback" className="dropdown-link" onClick={closeMenu}>
                <span className="dropdown-link-icon"><FeedbackIcon /></span>
                My Feedback
              </Link>
              <Link to="/support" className="dropdown-link" onClick={closeMenu}>
                <span className="dropdown-link-icon"><LightbulbIcon /></span>
                Support
              </Link>
              {user.role === 'admin' && (
                <Link to="/settings" className="dropdown-link" onClick={closeMenu}>
                  <span className="dropdown-link-icon"><SettingsIcon /></span>
                  Settings
                </Link>
              )}

              <button 
                className="dropdown-link" 
                onClick={handleLogout}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#e53e3e',
                  fontWeight: 500
                }}
              >
                <span className="dropdown-link-icon"><LogoutIcon /></span>
                Logout
              </button>
            </DropdownMenu>
          </div>
            </>
          )}
        </div>
      </nav>
    </>
  );
}

export default Navbar;
